// ConsentForm.tsx
import React, {useEffect, useState} from "react";
import {cn} from "@/lib/utils";
import axios from "@/lib/axios";
import {API_BASE_URL} from "@/config";
import {useParams, useNavigate} from 'react-router-dom';
import {cookieExistsAndValid, getCookie} from "@/utils/cookies";
import JourneyHeader from "@/components/JourneyHeader";

type ConsentDetails = {
	id: number;
	type: string;
	version: string;
};

const Terms = () => {
	const {enc_id} = useParams<{ enc_id: string }>();
	
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	
	// State for consent data
	const [content, setContent] = useState<string | null>(null);
	const [accepted, setAccepted] = useState<boolean>(false);
	const [consentDetails, setConsentDetails] = useState<ConsentDetails | null>(null);
	
	const navigate = useNavigate();
	
	// Fetch Terms data on mount
	useEffect(() => {
		const fetchConsentData = async () => {
			setLoading(true);
			setError(null);
			
			
			// Step 2: Fetch consent details with retry logic for 404
			let retries = 0;
			const maxRetries = 5;
			
			while (retries < maxRetries) {
				try {
					const response = await axios.post(`${API_BASE_URL}/api/get_terms/`, {});
					console.log("Consent details:", response.data);
					
					const {consent_details, content, accepted} = response.data;
					setConsentDetails(consent_details);
					setContent(content);
					// setAccepted(accepted);
					setLoading(false);
					return; // Success, exit the function
				} catch (err: any) {
					if (err.response?.status === 404 && retries < maxRetries - 1) {
						console.log(`404 error encountered. Retrying... (${retries + 1}/${maxRetries})`);
						retries++;
						// Add delay between retries (exponential backoff)
						await new Promise(resolve => setTimeout(resolve, 1000 * retries));
					} else {
						console.error("? Error fetching consent details:", err);
						setError(err.message || "Something went wrong");
						setLoading(false);
						return; // Fail after max retries or non-404 error
					}
				}
			}
			
			// If we reach here, all retries failed
			setError("Failed to fetch consent details after multiple attempts");
			setLoading(false);
		};
		
		fetchConsentData();
	}, []);
	
	// Submit consent acceptance
	const handleSubmit = async () => {
		// If checkbox is not ticked, do nothing
		if (!accepted) return;
		if (!consentDetails) return;
		try {
			setSubmitting(true);
			// 2. Use value of checkbox (accepted) in API call
			await axios.post(`${API_BASE_URL}/api/submit_consent/`, {
				enc_id,
				accepts_terms_and_privacy: accepted,
				consent_details: consentDetails,
			});
			navigate('/home');
		} catch (err: any) {
			setError(err.message || "Failed to submit consent");
		} finally {
			setSubmitting(false);
		}
	};
	
	
	// Different UI states
	if (loading) return <p>Loading...</p>;
	if (error) return <p className="text-red-500">{error}</p>;
	
	return (
		<div className="min-h-screen bg-intellend-white terms-content">
			{/* Header with logo */}
			<JourneyHeader />
			
			{/* Consent content */}
			<main className="max-w-3xl mx-auto p-4">
				<h1 className="text-xl font-semibold mb-4">Terms & Conditions</h1>
				
				<div
					className="border p-4 rounded-md bg-gray-50"
					// Insert backend consent text safely
					dangerouslySetInnerHTML={{__html: content || ""}}
				/>
			</main>
		</div>
	);
};

export default Terms;